import type { LucideIcon } from "lucide-react";

/// The three things the command menu can take you to. `nav` is a static
/// destination derived from the sidebar config, `person` is a directory row,
/// `workspace` switches the active workspace.
export type CommandKind = "nav" | "person" | "workspace";

export type CommandEntry = {
  /** Stable identity for recents and React keys, e.g. `nav:/$workspaceSlug/goals`. */
  id: string;
  kind: CommandKind;
  label: string;
  /** Resolved, navigable URL. No `$workspaceSlug` tokens left. */
  href: string;
  icon: LucideIcon;
  /** Extra match tokens. Nav labels are terse ("Insights"), so the words a
   *  user is likely to type ("analytics", "charts") live here. */
  keywords?: string[];
  /** Secondary line: job title, department, or "Switch workspace". */
  hint?: string;
  avatarUrl?: string | null;
};

const EXACT = 0;
const LABEL_PREFIX = 1;
const WORD_PREFIX = 2;
const SUBSTRING = 3;
const KEYWORD = 4;
const NO_MATCH = -1;

/// Lowercase and strip combining marks so "jose" matches "José". Diacritic
/// folding matters because display names come from an HR import, not from
/// what the person searching can type.
export function normalizeForMatch(value: string): string {
  return value
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "");
}

/// Lower is better. `NO_MATCH` (-1) means drop the entry. `normalizedTerm` is
/// pre-normalized by the caller so scanning a list does not re-normalize the
/// term once per entry.
export function scoreCommand(entry: CommandEntry, normalizedTerm: string): number {
  const label = normalizeForMatch(entry.label);
  if (label === normalizedTerm) return EXACT;
  if (label.startsWith(normalizedTerm)) return LABEL_PREFIX;
  if (label.split(" ").some((word) => word.startsWith(normalizedTerm))) return WORD_PREFIX;
  if (label.includes(normalizedTerm)) return SUBSTRING;
  // Keywords match on word prefix, not arbitrary substring. Substring matching
  // made short terms noisy: "ma" hit Reviews through "perforMAnce" and ranked
  // a page above the people the searcher was actually after.
  const keywords = entry.keywords ?? [];
  if (
    keywords.some((keyword) => startsAtWordBoundary(normalizeForMatch(keyword), normalizedTerm))
  ) {
    return KEYWORD;
  }
  return NO_MATCH;
}

/// The term must start at a word boundary but may run across words, so a
/// multi-word search ("api key") still reaches a multi-word keyword. Splitting
/// the keyword into words would make that impossible.
function startsAtWordBoundary(value: string, term: string): boolean {
  return ` ${value}`.includes(` ${term}`);
}

/// Ranks static commands locally, which is why `CommandDialog` disables cmdk's
/// own filter. People rows are NOT passed through this: the shore already
/// matched them on name and email, and re-scoring them against the display
/// name alone would drop legitimate hits.
export function matchCommands(entries: CommandEntry[], term: string): CommandEntry[] {
  const normalizedTerm = normalizeForMatch(term.trim());
  if (normalizedTerm.length === 0) return entries;

  return entries
    .map((entry, index) => ({ entry, index, score: scoreCommand(entry, normalizedTerm) }))
    .filter((scored) => scored.score !== NO_MATCH)
    .sort((a, b) => (a.score === b.score ? a.index - b.index : a.score - b.score))
    .map((scored) => scored.entry);
}
